<template>
  <fullscreen-dialog
    v-model="model"
    :title="user ? user.user_name : ''"
    :subtitle="`ID: ${user ? user.user_id : ''}`"
    hide-footer
  >
    <template #default>
      <v-card-text>
        <div v-if="!user">
          {{ $t("no_data") }}
        </div>

        <div v-else>
          <!-- User Info Card -->
          <v-card outlined class="mb-4">
            <v-card-text>
              <v-row v-if="currentUser.totals" dense justify="space-between">
                <v-col v-if="restaurant" cols="12" md="auto">
                  <div class="text-subtitle-2 grey--text mb-1">
                    {{ $t("restaurant") }}
                  </div>
                  <div class="text-h6">
                    {{ restaurant.name }}
                  </div>
                </v-col>
                <v-col cols="12" md="auto">
                  <div class="text-subtitle-2 grey--text mb-1">
                    {{ $t("orders") }}
                  </div>
                  <div class="text-h6 primary--text">
                    {{ filteredItems.length }}
                  </div>
                </v-col>
                <v-col cols="12" md="auto">
                  <div class="text-subtitle-2 grey--text mb-1">
                    {{ $t("spent_on_credits") }}
                  </div>
                  <div class="text-h6 error--text">
                    {{ calculatedSpentOnCredits }}
                  </div>
                </v-col>
                <v-col cols="12" md="auto">
                  <div class="text-subtitle-2 grey--text mb-1">
                    {{ $t("credit_balance") }}
                  </div>
                  <div class="text-h6 success--text">
                    {{
                      formatMoneyFromTotals(
                        currentUser.totals.current_credit_balance_cents,
                        currentUser.totals.current_credit_balance
                      )
                    }}
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Month Picker -->
          <v-row align="center">
            <v-col cols="auto">
              <month-picker v-model="date" color="white"></month-picker>
            </v-col>
            <v-col cols="4" md="3">
              <text-field
                v-model="search"
                kind="search"
                :label="$t('search')"
                color="white"
                clearable
              ></text-field>
            </v-col>
            <v-col align="end">
              <v-row no-gutters align="center" justify="end">
                <excel-button class="mr-2" @click="exportToExcel"></excel-button>
                <print-button @click="printReport"></print-button>
              </v-row>
            </v-col>
          </v-row>

          <!-- Orders Table -->
          <div class="text-h6 mb-3">{{ $t("orders") }}</div>
          <v-data-table
            :headers="orderHeaders"
            :items="filteredItems"
            :loading="loading"
            hide-default-footer
            :items-per-page="-1"
            class="striped-table ordera-box-shadow"
          >
            <template v-slot:item.items="{ item }">
              <div v-if="Array.isArray(item.items) && item.items.length">
                <div v-for="(it, idx) in item.items" :key="idx" class="mb-1">
                  <v-chip small label color="grey lighten-3" class="mr-1">
                    {{ it.quantity }}x
                  </v-chip>
                  <span>{{ it.name }}</span>
                </div>
              </div>
              <div v-else class="grey--text">-</div>
            </template>

            <template v-slot:item.date="{ item }">
              {{ localeDate(item.date) }}
            </template>

            <template v-slot:item.restaurant="{ item }">
              {{ item.restaurant || '-' }}
            </template>

            <template v-slot:item.total_cents="{ item }">
              {{ formatMoneyCents(item.total_cents) }}
            </template>

            <template v-slot:item.credits_spent_cents="{ item }">
              {{ formatMoneyCents(item.credits_spent_cents || item.credits_spent) }}
            </template>

            <template v-slot:item.credit_transactions="{ item }">
              <div
                v-if="
                  (Array.isArray(item.credit_transactions) &&
                    item.credit_transactions.length) ||
                  (Array.isArray(item.items) &&
                    item.items.some(
                      (it) =>
                        Array.isArray(it.credit_transactions) &&
                        it.credit_transactions.length
                    ))
                "
              >
                <!-- Order-level transactions -->
                <div
                  v-if="
                    Array.isArray(item.credit_transactions) &&
                    item.credit_transactions.length
                  "
                  class="mb-2"
                >
                  <div
                    v-for="tx in item.credit_transactions"
                    :key="'order-' + tx.id"
                    class="mb-1"
                  >
                    <v-chip x-small color="success" dark class="mr-1">
                      {{ formatMoneyCents(tx.amount_cents) }}
                    </v-chip>
                    <span>{{ tx.note }}</span>
                    <div class="text-caption grey--text mt-1">
                      {{ formatDateTime(tx.created_at) }}
                    </div>
                  </div>
                </div>

                <!-- Item-level transactions grouped by item -->
                <div v-if="Array.isArray(item.items)">
                  <div
                    v-for="(it, idx) in item.items"
                    :key="'item-' + (it.id || idx)"
                    class="mb-2"
                  >
                    <div
                      v-if="
                        Array.isArray(it.credit_transactions) &&
                        it.credit_transactions.length
                      "
                    >
                      <div
                        v-for="tx in it.credit_transactions"
                        :key="'itemtx-' + tx.id"
                        class="mb-1"
                      >
                        <v-chip x-small color="info" dark class="mr-1">
                          {{ formatMoneyCents(tx.amount_cents) }}
                        </v-chip>
                        <span>{{ tx.note }}</span>
                        <div class="text-caption grey--text mt-1">
                          {{ formatDateTime(tx.created_at) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="grey--text">-</div>
            </template>
          </v-data-table>
        </div>
      </v-card-text>
    </template>
  </fullscreen-dialog>
</template>

<script>
import FullscreenDialog from "@/components/dialogs/FullscreenDialog.vue";
import reportService from "@/services/report";
import hasModel from "@/mixins/hasModel";
import hasSnackbar from "@/mixins/hasSnackbar";
import hasExcel from "@/mixins/hasExcel";
import hasPrinting from "@/mixins/hasPrinting";

export default {
  components: {
    FullscreenDialog,
  },
  mixins: [hasModel, hasSnackbar, hasExcel, hasPrinting],
  props: {
    user: {
      type: Object,
      default: null,
    },
    companyId: {
      type: [Number, String],
      default: null,
    },
  },
  data: () => ({
    date: new Date().toISOString().substr(0, 7),
    loading: false,
    fetchedUser: null,
    search: "",
  }),
  computed: {
    filteredItems() {
      return this.customSearch(
        this.ordersWithItemsSearch,
        this.orderHeaders,
        this.search,
        ["items_search"]
      );
    },
    month() {
      return parseInt(this.date.split("-")[1]);
    },
    year() {
      return parseInt(this.date.split("-")[0]);
    },
    monthName() {
      return this.localeDate(this.date + "-01", { month: "long", year: "numeric" });
    },
    currentUser() {
      return this.fetchedUser || this.user || {};
    },
    orderHeaders() {
      const headers = [
        { text: this.$t("id"), value: "id" },
        { text: this.$t("date"), value: "date" },
      ];
      
      if (this.isCompanyUser) {
        headers.push({ text: this.$t("restaurant"), value: "restaurant" });
      }
      
      headers.push(
        { text: this.$t("ordered"), value: "items" },
        { text: this.$t("total"), value: "total_cents", align: "right" },
        {
          text: this.$t("spent_on_credits"),
          value: "credits_spent_cents",
          align: "right",
        },
        {
          text: this.$t("credit_transactions"),
          value: "credit_transactions",
        }
      );
      
      return headers;
    },
    restaurant() {
      return this.$store.state.restaurant;
    },
    isCompanyUser() {
      return this.$store.state.user.user?.type === 'company';
    },
    calculatedSpentOnCredits() {
      const orders = this.ordersWithItemsSearch;
      const totalSpentCents = orders.reduce((sum, order) => {
        const spent = order.credits_spent_cents || order.credits_spent || 0;
        return sum + (typeof spent === "number" ? spent : parseFloat(spent) * 100);
      }, 0);
      return this.formatMoneyCents(totalSpentCents);
    },
    ordersWithItemsSearch() {
      const orders = this.currentUser.orders_valid || this.currentUser.orders || [];
      let filtered = orders.map((order) => ({
        ...order,
        items_search: Array.isArray(order.items)
          ? order.items.map((it) => it.name).join(", ")
          : "",
      }));
      
      // Ako je restaurant user, filtriraj samo po tom restoranu
      if (!this.isCompanyUser && this.restaurant) {
        filtered = filtered.filter((item) => item?.restaurant === this.restaurant?.name);
      }
      
      return filtered.sort((a, b) => {
        const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          return dateA - dateB;
        });
    },
  },
  methods: {
    formatDateTime(d) {
      if (!d) return "";
      return this.localeDate(d, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    formatMoneyCents(cents) {
      if (cents === null || cents === undefined || cents === "") return "";
      const n = Number(cents);
      if (Number.isNaN(n)) return "";
      return (n / 100).toFixed(2);
    },
    formatMoneyFromTotals(centsVal, fallback) {
      if (centsVal !== null && centsVal !== undefined && centsVal !== "") {
        return this.formatMoneyCents(centsVal);
      }
      if (fallback !== null && fallback !== undefined && fallback !== "") {
        const n = Number(fallback);
        if (Number.isNaN(n)) return "";
        return n.toFixed(2);
      }
      return "";
    },
    exportToExcel() {
      const exportHeaders = this.orderHeaders
        .filter((h) => h.value !== "credit_transactions")
        .map((h) => ({
          text: h.text,
          value: h.value,
          align: h.align,
        }));

      const exportItems = this.filteredItems.map((item) => ({
        id: item.id,
        date: this.localeDate(item.date),
        items: Array.isArray(item.items)
          ? item.items.map((it) => `${it.quantity}x ${it.name}`).join(", ")
          : "-",
        total_cents: this.formatMoneyCents(item.total_cents),
        credits_spent_cents: this.formatMoneyCents(
          item.credits_spent_cents || item.credits_spent
        ),
      }));

      const totalRow = {
        id: "",
        date: "",
        items: this.$t("total"),
        total_cents: this.formatMoneyCents(
          this.filteredItems.reduce((sum, item) => sum + (item.total_cents || 0), 0)
        ),
        credits_spent_cents: this.calculatedSpentOnCredits,
      };

      this.downloadExcel({
        fileName: `${this.user.user_name}_${this.restaurant?.name || ""}_${
          this.monthName
        }.xlsx`,
        sheets: [
          {
            name: this.user.user_name,
            headers: exportHeaders,
            items: [...exportItems, totalRow],
            title: `${this.user.user_name} - ${this.restaurant?.name || ""}`,
            subtitle: this.monthName,
          },
        ],
      });
    },
    printReport() {
      const printHeaders = this.orderHeaders.filter(
        (h) => h.value !== "credit_transactions"
      );

      const printItems = this.filteredItems.map((item) => ({
        id: item.id,
        date: this.localeDate(item.date),
        items: Array.isArray(item.items)
          ? item.items.map((it) => `${it.quantity}x ${it.name}`).join(", ")
          : "-",
        total_cents: this.formatMoneyCents(item.total_cents),
        credits_spent_cents: this.formatMoneyCents(
          item.credits_spent_cents || item.credits_spent
        ),
      }));

      const totalRow = {
        id: "",
        date: "",
        items: this.$t("total"),
        total_cents: this.formatMoneyCents(
          this.filteredItems.reduce((sum, item) => sum + (item.total_cents || 0), 0)
        ),
        credits_spent_cents: this.calculatedSpentOnCredits,
      };

      this.printDocument({
        title: `${this.user.user_name} - ${this.restaurant?.name || ""}`,
        subtitle: this.monthName,
        tables: [
          {
            headers: printHeaders,
            items: [...printItems, totalRow],
          },
        ],
      });
    },
    async fetchUserData() {
      if (!this.user || !this.user.user_id) return;

      this.loading = true;
      try {
        const firstDay = `${this.year}-${String(this.month).padStart(2, "0")}-01`;
        const lastDayDate = new Date(this.year, this.month, 0);
        const lastDay = `${this.year}-${String(this.month).padStart(2, "0")}-${String(
          lastDayDate.getDate()
        ).padStart(2, "0")}`;

        const params = {
          date_from: firstDay,
          date_to: lastDay,
        };

        if (this.companyId) {
          params.company_id = this.companyId;
        }

        const { data } = await reportService.getCreditUser(params);

        // Find the current user in the response
        let userArray = [];
        if (Array.isArray(data.users)) {
          userArray = data.users;
        } else if (data.user_id) {
          userArray = [data];
        } else if (Array.isArray(data)) {
          userArray = data;
        }

        const foundUser = userArray.find((u) => u.user_id === this.user.user_id);
        if (foundUser) {
          const orders = Array.isArray(foundUser.orders)
            ? foundUser.orders.filter((o) => o && o.id)
            : [];
          this.fetchedUser = {
            ...foundUser,
            orders_valid: orders,
            totals: foundUser.totals || {},
          };
        } else {
          this.fetchedUser = {
            ...this.user,
            orders_valid: [],
            totals: {},
          };
        }
      } catch (error) {
        this.showErrorSnackbar(error, this.$t("error_loading_report"));
        this.fetchedUser = null;
      } finally {
        this.loading = false;
      }
    },
  },
  watch: {
    date: {
      handler() {
        this.fetchUserData();
      },
    },
    model: {
      handler(val) {
        if (val && this.user) {
          this.fetchedUser = null;
        }
      },
    },
  },
};
</script>
